import { useEffect, useState } from "react";

/** Tailwind `md` breakpoint — the app-wide desktop/mobile cutover. */
export const DESKTOP_QUERY = "(min-width: 768px)";

/**
 * useIsDesktop — reactive `(min-width: 768px)` match.
 *
 * Listens on the matchMedia query so a viewport flip between desktop and
 * mobile re-renders with the right layout (a one-shot `window.innerWidth`
 * read would go stale on resize / orientation change).
 *
 * SSR/jsdom-safe: `window.matchMedia` access is gated; environments without
 * it fall back to `false` so the mobile branch renders by default
 * (fail-closed posture — same as AdminLayout's original local hook).
 */
export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(DESKTOP_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const handler = (e) => setIsDesktop(e.matches);
    // Sync once on mount in case the breakpoint flipped between the
    // useState initializer and the effect attaching its listener.
    setIsDesktop(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isDesktop;
}
